import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Shield, ShieldCheck, Building2, Radio } from "lucide-react";
import { Reveal, Lines, Counter, MarqueeRow, HeroVideo } from "../components/motion";
import { CtaBand, FaqList, LegalitaBadge } from "../components/shared";
import { QuoteLink } from "../components/QuotePanel";
import { imgProps } from "../components/media";
import { CLIENTS_ROW1, CLIENTS_ROW2, CERTS, AIRPORTS, HOME_SERVICES, STATS, FAQS, img, gruppo, aurea } from "../data/site";

const PILLARS = [
  ["01", "Vigilanza armata", "Guardie particolari giurate per pronto intervento, antirapina, piantonamento, trasporto e custodia valori.", "/servizi", ""],
  ["02", "Vigilanza non armata", "Portierato, reception, controllo accessi e personale per eventi, anche multilingue.", "/servizi", "tone-w"],
  ["03", "Obiettivi sensibili", "Aeroporti, porti, stazioni e infrastrutture strategiche, con personale certificato ENAC.", "/obiettivi-sensibili", "tone-a"],
  ["04", "Centrale operativa H24", "Due centrali certificate UNI CEI EN 50518, a San Cesareo e Bascapè, sempre in linea.", "/centrale-operativa", "tone-w"],
];

const PIL_ICON = [Shield, ShieldCheck, Building2, Radio];

export default function Home() {
  const reduce = useReducedMotion();
  return (
    <>
      <section className="hero">
        <HeroVideo src="/assets/video/loop-home-1080.mp4" poster="/assets/video/loop-home-poster.jpg" zoom />
        <div className="hero-in">
          <motion.p className="h-kick" initial={reduce ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduce ? 0 : 0.4, delay: reduce ? 0 : 0.1 }}>
            <span className="dash" /> Istituto di vigilanza privata · dal 1992 <span className="dash" />
          </motion.p>
          <Lines lines={["Al vostro fianco,", "ogni giorno,", "dal 1992."]} accentLast baseDelay={0.25} />
          <motion.p
            className="hero-sub"
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduce ? 0 : 0.4, delay: reduce ? 0 : 0.5, ease: [0.2, 0.7, 0.2, 1] }}
          >
            Guardie particolari giurate, sicurezza aeroportuale, trasporto valori e due centrali operative certificate, per privati, imprese e Pubblica Amministrazione.
          </motion.p>
          <motion.div
            className="hero-ctas"
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduce ? 0 : 0.4, delay: reduce ? 0 : 0.65, ease: [0.2, 0.7, 0.2, 1] }}
          >
            <QuoteLink className="btn" testid="hero-cta-preventivo">
              Richiedi un preventivo
            </QuoteLink>
            <Link to="/servizi" className="btn ghost" data-testid="hero-cta-servizi">
              Scopri i servizi
            </Link>
          </motion.div>
          <motion.div
            className="hero-trust"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: reduce ? 0 : 0.4, delay: reduce ? 0 : 0.85 }}
            data-testid="hero-trust"
          >
            <span>Licenza prefettizia n. 283167</span>
            <span className="dot" aria-hidden="true" />
            <span>Centrali certificate UNI CEI EN 50518</span>
            <span className="dot" aria-hidden="true" />
            <span>6 aeroporti presidiati</span>
          </motion.div>
        </div>
        <div className="scrollcue" aria-hidden="true">
          Scorri
          <span className="bar" />
        </div>
      </section>

      <section className="pillars" aria-label="I nostri ambiti">
        <h2 className="sr-only">I nostri ambiti di servizio</h2>
        <div className="pillars-grid">
          {PILLARS.map(([num, title, text, to, tone], i) => {
            const Icon = PIL_ICON[i];
            return (
              <motion.div key={num} initial={reduce ? false : { opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: reduce ? 0 : 0.4, delay: reduce ? 0 : i * 0.09, ease: "easeOut" }}>
                <Link to={to} className={`pillar ${tone}`} data-testid={`pillar-${num}`}>
                  <span className="num">{num}</span>
                  <Icon size={30} style={{ color: tone === "tone-a" ? "#06121F" : "#1EA7E8", marginBottom: 16 }} aria-hidden="true" />
                  <h3>{title}</h3>
                  <p>{text}</p>
                  <span className="more">Scopri di più</span>
                </Link>
              </motion.div>
            );
          })}
        </div>
        <div className="pillar-line" aria-hidden="true" />
      </section>

      <section className="sec" data-testid="home-chisiamo">
        <div className="wrap" style={{ display: "flex", flexWrap: "wrap", gap: "48px 90px", alignItems: "center" }}>
          <Reveal style={{ flex: "1 1 500px", minWidth: 0 }}>
            <p className="kick">Chi siamo</p>
            <h2 className="h2">Dal 1992 la sicurezza è il nostro unico mestiere.</h2>
            <p className="lead" style={{ margin: "30px 0 22px" }}>
              Siamo nati nel 1992 e oggi siamo Orion Security, istituto di vigilanza privata autorizzato con licenza prefettizia n. 283167 e parte del gruppo Aurea Holding 3.
            </p>
            <p style={{ color: "var(--muted)", marginBottom: 40 }}>
              Uomini formati, strutture di proprietà e centrali operative ridondate: tutto quello che serve per proteggere una casa, un'azienda o un aeroporto, senza interruzioni.
            </p>
            <Link to="/chi-siamo" className="btn dark" data-testid="home-chisiamo-btn">
              La nostra storia
            </Link>
          </Reveal>
          <Reveal delay={0.1} style={{ flex: "1 1 460px", minWidth: 0 }}>
            <div className="stats">
              {STATS.map((s, i) => (
                <div className="stat" key={i} data-testid={`home-stat-${i}`}>
                  <Counter to={s.to} plain={s.plain} />
                  <p>{s.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section style={{ position: "relative", overflow: "hidden" }} data-testid="home-servizi">
        <div className="phlight" aria-hidden="true">
          <img {...imgProps(img("flotta-pattuglie.jpg"), { sizes: "100vw" })} alt="" />
        </div>
        <div style={{ position: "relative" }} className="wrap">
          <Reveal className="sec-head center">
            <p className="kick">Servizi di vigilanza</p>
            <h2 className="h2">Scopri i nostri servizi</h2>
          </Reveal>
          <div className="cards">
            {HOME_SERVICES.map(([name, text, to], i) => (
              <motion.div key={name} initial={reduce ? false : { opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: reduce ? 0 : 0.4, delay: reduce ? 0 : (i % 3) * 0.08, ease: "easeOut" }}>
                <Link to={to} className="card" data-testid={`home-svc-${i}`}>
                  <span className="num">0{i + 1}</span>
                  <h3>{name}</h3>
                  <p>{text}</p>
                  <span className="more">Approfondisci</span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="sec-blue sec" data-testid="home-obiettivi">
        <div className="wrap" style={{ display: "flex", flexWrap: "wrap", gap: "56px 90px", alignItems: "center" }}>
          <Reveal style={{ flex: "1 1 460px", minWidth: 0 }}>
            <p className="kick">Obiettivi sensibili</p>
            <h2 className="h2">Dove passa il Paese, ci siamo noi.</h2>
            <p className="lead" style={{ margin: "30px 0 40px" }}>
              Varchi, passeggeri, bagagli e merci negli scali principali, con personale certificato da ENAC e Polizia di Stato. E siamo stati scelti per le visite di Stato più delicate.
            </p>            <Link to="/obiettivi-sensibili" className="btn" data-testid="home-obiettivi-btn">
              Scopri gli obiettivi sensibili
            </Link>
          </Reveal>
          <Reveal delay={0.1} style={{ flex: "1 1 480px", minWidth: 0 }}>
            <div className="airports">
              {AIRPORTS.map(([code, name]) => (
                <div className="airport" key={code} data-testid={`airport-${code}`}>
                  <p className="code">{code}</p>
                  <p>{name}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section style={{ padding: "110px 0 100px", overflow: "hidden" }} data-testid="home-clienti">
        <Reveal className="sec-head center" style={{ marginBottom: 50 }}>
          <p className="kick">Clienti</p>
          <h2 className="h2">Ci hanno già scelto</h2>
        </Reveal>
        <div className="mrq">
          <MarqueeRow items={CLIENTS_ROW1} dir={1} testid="clienti-marquee-1" />
          <MarqueeRow items={CLIENTS_ROW2} dir={-1} testid="clienti-marquee-2" />
        </div>
      </section>

      <section className="sec-mist sec" data-testid="home-certificazioni">
        <div className="wrap" style={{ display: "flex", flexWrap: "wrap", gap: "48px 80px", alignItems: "center" }}>
          <Reveal style={{ flex: "1 1 400px", minWidth: 0 }}>
            <p className="kick">Certificazioni</p>
            <h2 className="h2">Qualità e sicurezza certificate, non dichiarate.</h2>
            <p style={{ margin: "28px 0 36px", color: "var(--muted)" }}>
              UNI 10891 per gli istituti di vigilanza, UNI CEI EN 50518 per le centrali operative, ISO 9001, 14001, 45001, 50001, 27001, SA 8000, UNI/PdR 125 sulla parità di genere, il rating di legalità AGCM e codice fornitore NATO.
            </p>
            <Link to="/chi-siamo" className="btn dark" data-testid="home-certs-btn">
              Licenze e certificazioni
            </Link>
          </Reveal>
          <Reveal delay={0.1} style={{ flex: "1 1 560px", minWidth: 0 }}>
            <div className="certs">
              {CERTS.map(([slug, nm, dsc]) => (
                <div className="cet" key={slug} data-testid={`cert-${slug}`}>
                  <img {...imgProps(`/assets/certificazioni/${slug}.png`, { sizes: "240px" })} alt={`Certificazione ${nm}`} />
                  <p className="nm">{nm}</p>
                  <p>{dsc}</p>
                </div>
              ))}
              <div className="cet" data-testid="cert-rating-legalita">
                <LegalitaBadge />
                <p className="nm">Rating di legalità</p>
                <p>
                  Attribuito dall'Autorità Garante della Concorrenza e del Mercato (AGCM) alle imprese che rispettano elevati standard di legalità, trasparenza e responsabilità: due stelle su tre, con il segno più dei requisiti premiali.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="sec" data-testid="home-faq">
        <div className="wrap" style={{ display: "flex", flexWrap: "wrap", gap: "40px 90px" }}>
          <Reveal style={{ flex: "1 1 360px" }}>
            <p className="kick">Domande frequenti</p>
            <h2 className="h2">Le risposte che cerchi</h2>
            <p style={{ margin: "26px 0 0", color: "var(--muted)" }}>
              Per tutto il resto, la nostra assistente AI risponde al telefono e in chat, a qualsiasi ora.
            </p>
          </Reveal>
          <Reveal delay={0.1} style={{ flex: "2 1 560px", minWidth: 0 }}>
            <FaqList items={FAQS.home} testid="home-faq" />
          </Reveal>
        </div>
      </section>

      <section className="sec-blue sec" style={{ paddingBottom: 110 }} data-testid="home-gruppo">
        <div className="wrap">
          <Reveal>
            <p className="kick" style={{ color: "var(--gold)", display: "flex", alignItems: "center", gap: 14 }}>
              <img {...imgProps(aurea("emblema.png"), { sizes: "30px" })} alt="" style={{ height: 30, width: "auto" }} /> Le società del Gruppo Aurea Holding 3
            </p>
          </Reveal>
          <div className="gcards" style={{ marginTop: 34 }}>
            <Reveal>
              <a href="https://www.axentrasrl.com" className="gcard" target="_blank" rel="noreferrer" data-testid="gruppo-axentra">
                <img {...imgProps(gruppo("axentra-scuro.png"), { sizes: "(max-width: 760px) 92vw, 480px" })} alt="Axentra" />
                <p>La tecnologia del gruppo: videosorveglianza AI, allarmi, droni e robotica, già pronti per la nostra centrale.</p>
                <span className="more">axentrasrl.com</span>
              </a>
            </Reveal>
            <Reveal delay={0.08}>
              <a href="https://www.inquiria.eu" className="gcard" target="_blank" rel="noreferrer" data-testid="gruppo-astra">
                <img {...imgProps(gruppo("astra.png"), { sizes: "(max-width: 760px) 92vw, 480px" })} alt="Astra Investigazioni" className="tall" />
                <p>Le investigazioni del gruppo: indagini aziendali, business information e computer forensic.</p>
                <span className="more">inquiria.eu</span>
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBand testid="home-cta" />
    </>
  );
}
